import merge from "lodash/merge";
import { mergeDeep } from "app/utils/objectHelpers";
import { DSOK_CUSTOMER_FIELD_SCHEMA } from "../smartkioskFields/SmartkioskCustomerFields";
import { getLocationsFieldSchema } from "../smartkioskFields/SmartkioskLocationFields";
import { getDevicesFieldSchema } from "../fields/DevicesField";
import {
  mergeWorkspacesSources,
  normalizeWorkspacesForUi,
} from "../utils/normalizeDsokWorkspaces";
import { SETTINGS_FIELD_SCHEMA } from "../smartkioskFields/SmartkioskSettingsFields";
import { getMaintenanceFieldSchema } from "../fields/MaintenanceField";
import { getPrinterSettingsFieldSchema } from "../fields/PrinterSettingsFields";
import { ORDER_MANAGEMENT_FIELD_SCHEMA } from "../fields/OrderManagementField";
import { INTEGRATIONS_FIELD_SCHEMA } from "../smartkioskFields/SmartkioskIntegrationFields";
import { DEFAULT_COLORS } from "../smartkioskFields/colorsFieldConfig";
import { sanitizeLogoUrlString } from "./sanitizeLogoUrlString";

/**
 * Get initial values for SmartkioskDetails form based on customer DSOK settings
 * @param {Object} customer - Customer data from API
 * @returns {Object} Initial values for Formik
 */
export const getSmartkioskInitialValues = (customer) => {
  if (!customer) {
    return null;
  }

  const dsokSettings = customer.dsokSettings || {};

  const ensureArray = (v) =>
    Array.isArray(v) ? v : v != null && v !== "" ? [v] : [];

  const mergedCustomer = mergeDeep(
    DSOK_CUSTOMER_FIELD_SCHEMA,
    dsokSettings.customer || {},
  );
  delete mergedCustomer.enableDynamicComponents;
  delete mergedCustomer.enableStatusScreen;

  const baseCustomer = {
    ...mergedCustomer,
    communicationType:
      mergedCustomer.communicationType === "DLA" ? "DLA" : "ReverseSSH",
    datavisionPosEnabled: mergedCustomer.datavisionPosEnabled === true,
    pickupDisplayEnabled: mergedCustomer.pickupDisplayEnabled === true,
    kitchenDisplayEnabled: mergedCustomer.kitchenDisplayEnabled === true,
    kitchenPrintingEnabled: mergedCustomer.kitchenPrintingEnabled === true,
    customerUISettings: merge(
      getMaintenanceFieldSchema(
        Object.keys(dsokSettings.customer?.customerUISettings || {}),
      ),
      dsokSettings.customer?.customerUISettings || {},
    ),
    locations: merge(
      getLocationsFieldSchema(dsokSettings.customer?.locations),
      dsokSettings.customer?.locations || {},
    ),
    devices: merge(
      getDevicesFieldSchema(dsokSettings.customer?.devices),
      dsokSettings.customer?.devices || {},
    ),
    workspaces: (() => {
      const merged = mergeWorkspacesSources(
        dsokSettings.customer?.workspaces,
        dsokSettings.workspaces,
      );
      if (!merged || typeof merged !== "object") {
        return { data: [] };
      }
      return normalizeWorkspacesForUi(merged);
    })(),
  };

  const customerForForm = {
    ...baseCustomer,
    auth: ensureArray(baseCustomer.auth),
    paymentMethods: ensureArray(baseCustomer.paymentMethods),
  };
  if (typeof customerForForm.mainLogo === "string") {
    customerForForm.mainLogo = sanitizeLogoUrlString(customerForForm.mainLogo);
  }
  if (typeof customerForForm.tableImage === "string") {
    customerForForm.tableImage = sanitizeLogoUrlString(
      customerForForm.tableImage,
    );
  }

  return {
    // Customer basic information (auth/paymentMethods normalized to array for multiselect)
    customer: customerForForm,

    // Main settings - includes all configuration options
    settings: (() => {
      // Create a deep copy of the schema without dynamicComponents and categoriesDisplaySettings
      // NOTE: must deep-clone smartkiosk to avoid mutating the exported module constant
      const schemaWithoutDynamicComponents = {
        ...SETTINGS_FIELD_SCHEMA,
        smartkiosk: SETTINGS_FIELD_SCHEMA.smartkiosk
          ? { ...SETTINGS_FIELD_SCHEMA.smartkiosk }
          : undefined,
      };
      if (schemaWithoutDynamicComponents.smartkiosk) {
        delete schemaWithoutDynamicComponents.smartkiosk.dynamicComponents;
        delete schemaWithoutDynamicComponents.smartkiosk
          .categoriesDisplaySettings;
      }

      const mergedSettings = mergeDeep(
        schemaWithoutDynamicComponents,
        dsokSettings.settings || {},
      );

      // Now handle dynamicComponents separately
      if (dsokSettings.settings?.smartkiosk?.dynamicComponents) {
        if (!mergedSettings.smartkiosk) {
          mergedSettings.smartkiosk = {};
        }

        const originalDynamicComponents =
          dsokSettings.settings.smartkiosk.dynamicComponents;

        // Convert array to object format if needed
        if (Array.isArray(originalDynamicComponents)) {
          const dynamicComponentsObj = {};
          originalDynamicComponents.forEach((component, index) => {
            dynamicComponentsObj[index + 1] = component;
          });
          mergedSettings.smartkiosk.dynamicComponents = dynamicComponentsObj;
        } else {
          mergedSettings.smartkiosk.dynamicComponents =
            originalDynamicComponents;
        }
      }

      // Handle categoriesDisplaySettings separately
      if (dsokSettings.settings?.smartkiosk?.categoriesDisplaySettings) {
        if (!mergedSettings.smartkiosk) {
          mergedSettings.smartkiosk = {};
        }

        const originalCategoriesDisplaySettings =
          dsokSettings.settings.smartkiosk.categoriesDisplaySettings;

        // Convert array to object format if needed
        if (Array.isArray(originalCategoriesDisplaySettings)) {
          const categoriesDisplaySettingsObj = {};
          originalCategoriesDisplaySettings.forEach((category, index) => {
            categoriesDisplaySettingsObj[index + 1] = category;
          });
          mergedSettings.smartkiosk.categoriesDisplaySettings =
            categoriesDisplaySettingsObj;
        } else if (
          originalCategoriesDisplaySettings &&
          typeof originalCategoriesDisplaySettings === "object" &&
          !Object.keys(originalCategoriesDisplaySettings).every((k) =>
            /^\d+$/.test(String(k))
          )
        ) {
          // Backend returned flat object (e.g. after save); UI expects keyed { "1": {...} }
          mergedSettings.smartkiosk.categoriesDisplaySettings = {
            1: originalCategoriesDisplaySettings,
          };
        } else {
          mergedSettings.smartkiosk.categoriesDisplaySettings =
            originalCategoriesDisplaySettings;
        }
      }

      return mergedSettings;
    })(),

    // Maintenance settings
    maintenance: merge(
      getMaintenanceFieldSchema(
        Object.keys(dsokSettings.maintenance?.message || {}),
      ),
      dsokSettings.maintenance || {},
    ),

    // Printer settings
    printerSettings: merge(
      getPrinterSettingsFieldSchema(
        Object.keys(dsokSettings.printerSettings?.message || {}),
      ),
      dsokSettings.printerSettings || {},
    ),

    // Integrations configuration
    integrations: merge(
      INTEGRATIONS_FIELD_SCHEMA,
      dsokSettings.integrations || {},
    ),

    // Colors configuration - separate from settings for better organization
    colors: merge(
      { ...DEFAULT_COLORS },
      dsokSettings.settings?.colors || {},
    ),

    // UI Settings - separate from main settings
    uiSettings: merge(
      {
        welcomeTextSize: 58,
        welcomeTextColor: "#f0f0f0",
        buttonBackgroundColor: "#de4f44",
        buttonFontColor: "#f0f0f0",
        fontColorPrimary: "#1A1A1A",
        fontColorSecondary: "#de4f44",
        priceColor: "#de4f44",
        priceListSize: 16,
      },
      dsokSettings.uiSettings || {},
    ),
  };
};

/**
 * Get validation schema for SmartkioskDetails form
 * @param {Function} t - Translation function
 * @returns {Object} Yup validation schema
 */
export const getSmartkioskValidationSchema = (t) => {
  // Import validation schemas here if needed
  // This can be expanded based on requirements
  return {};
};

export default getSmartkioskInitialValues;
